import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, WA_LINK } from "@/constants/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const onDark = !scrolled && !open && pathname === "/";
  const linkBase = onDark ? "text-[#FAF8F5]/75 hover:text-[#FAF8F5]" : "text-[#1A1918]/70 hover:text-[#1A1918]";

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open
          ? "bg-[rgba(247,245,240,0.9)] shadow-[0_1px_0_rgba(22,21,20,0.08)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="RistrutturaCasa — Home">
          <Logo light={onDark} />
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-300 ${
                  isActive ? "text-[#C85A32]" : linkBase
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            data-testid="nav-call-button"
            href={PHONE_TEL}
            className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-5 py-2.5 text-sm font-semibold text-[#FAF8F5] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#A8481F]"
          >
            <Phone size={15} strokeWidth={2.2} />
            {PHONE_DISPLAY}
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          className={`md:hidden ${onDark ? "text-[#FAF8F5]" : "text-[#1A1918]"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-t border-[#1A1918]/10 bg-[rgba(247,245,240,0.98)] backdrop-blur-xl md:hidden"
          >
            <nav className="flex flex-col gap-1 px-5 py-6" aria-label="Menu mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl font-semibold ${
                        isActive ? "text-[#C85A32]" : "text-[#1A1918]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-4 flex flex-col gap-3">
                <a
                  data-testid="mobile-call-button"
                  href={PHONE_TEL}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C85A32] px-6 py-3.5 text-sm font-semibold text-[#FAF8F5]"
                >
                  <Phone size={16} /> Chiama Ora — {PHONE_DISPLAY}
                </a>
                <a
                  data-testid="mobile-whatsapp-button"
                  href={WA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1A1918]/25 px-6 py-3.5 text-sm font-semibold text-[#1A1918]"
                >
                  <MessageCircle size={16} /> Scrivi su WhatsApp
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
